import type { ApprovalStat } from '@/payload-types'

export interface StatsCalculationMethod {
  rateFormula: string
}

export interface VisaCategoryStat {
  id: string
  label: string
  casesFiled: number
  casesDecided: number
  casesApproved: number
  serotteApprovalRate: number
  showNationalComparison: boolean
  nationalComparisonNote: string | null
  nationalApprovalRate: number | null
  nationalAgency: string
  nationalFiscalYear: string
  nationalPrimaryDataset: string
  visaPageUrl: string
  roadmapSlug: string | null
  roadmapUrl: string | null
  roadmapImageUrl: string | null
}

const CASE_TYPE_LINKS: Record<string, { visaPageUrl: string; roadmapSlug: string }> = {
  'O-1': { visaPageUrl: '/visas/o-1', roadmapSlug: 'o-1-visa-roadmap' },
  'EB-1A': { visaPageUrl: '/visas/eb-1', roadmapSlug: 'eb-1a-green-card-roadmap' },
  'EB-1C': {
    visaPageUrl: '/visas/eb-1',
    roadmapSlug: 'eb-1c-green-card-roadmap-for-multinational-managers-executives',
  },
  'H-1B': { visaPageUrl: '/visas/h-1b', roadmapSlug: 'h-1b-visa-roadmap' },
  'L-1': { visaPageUrl: '/visas/l-1', roadmapSlug: 'l-1-visa-roadmap' },
  'E-2': { visaPageUrl: '/visas/e-2', roadmapSlug: 'e-2-visa-roadmap' },
  TN: { visaPageUrl: '/visas/tn', roadmapSlug: 'tn-visa-roadmap' },
  PERM: { visaPageUrl: '/visas/perm', roadmapSlug: 'perm-green-card-roadmap-timeline' },
  AOS: { visaPageUrl: '/visas/aos', roadmapSlug: 'aos-green-card-roadmap' },
}

/** All roadmap guide slugs referenced by case types (for fetching hero images). */
export const ROADMAP_SLUGS = Array.from(
  new Set(Object.values(CASE_TYPE_LINKS).map((entry) => entry.roadmapSlug)),
)

function resolveCaseTypeLinks(label: string) {
  const links = CASE_TYPE_LINKS[label] ?? CASE_TYPE_LINKS[label.toUpperCase()]
  const roadmapSlug = links?.roadmapSlug ?? null
  return {
    visaPageUrl: links?.visaPageUrl ?? '/visas',
    roadmapSlug,
    roadmapUrl: roadmapSlug ? `/guides/${roadmapSlug}` : null,
  }
}

function slugify(label: string): string {
  return label.toLowerCase().replace(/\s+/g, '-')
}

export function mapCalculationMethod(): StatsCalculationMethod {
  return {
    rateFormula:
      'Approval rate = Approved ÷ Adjudicated. Pending and withdrawn cases are excluded.  Only cases filed under the current administration are included in the listed approval rate.',
  }
}

export function mapCaseTypesToStats(global: ApprovalStat): VisaCategoryStat[] {
  const caseTypes = global.caseTypes ?? []

  return caseTypes.map((caseType) => {
    const showNationalComparison =
      caseType.showNationalComparison !== false && caseType.nationalApprovalRate != null

    const links = resolveCaseTypeLinks(caseType.label)

    return {
      id: slugify(caseType.label),
      label: caseType.label,
      casesFiled: caseType.casesFiled ?? 0,
      casesDecided: caseType.casesDecided,
      casesApproved: caseType.casesApproved,
      serotteApprovalRate: caseType.approvalRate,
      showNationalComparison,
      nationalComparisonNote: showNationalComparison
        ? null
        : caseType.nationalComparisonNote ?? null,
      nationalApprovalRate: caseType.nationalApprovalRate ?? null,
      nationalAgency: caseType.nationalAgency ?? '',
      nationalFiscalYear: caseType.nationalFiscalYear ?? '',
      nationalPrimaryDataset: caseType.nationalPrimaryDataset ?? '',
      visaPageUrl: links.visaPageUrl,
      roadmapSlug: links.roadmapSlug,
      roadmapUrl: links.roadmapUrl,
      roadmapImageUrl: null,
    }
  })
}

export function formatRate(rate: number): string {
  return `${rate % 1 === 0 ? rate.toFixed(0) : rate.toFixed(1)}%`
}

export function formatCasesFiled(count: number): string {
  return `${count.toLocaleString()} ${count === 1 ? 'case' : 'cases'} filed`
}
